/**
 * The home's delegation panels, as words (migration 980, roadmap D-4).
 *
 * Pure functions over the rows fetchMyDelegations reads. Whether a delegation
 * is in force, expired or waiting for acceptance, and how many whole days it
 * has left, comes from the database (computed on read); nothing here does date
 * arithmetic on the end date beyond formatting it.
 */

import type { MyDelegation } from '../../../lib/decisionRightDelegations';
import {
  CANONICAL_DECISION_CLASS_LABELS,
  CANONICAL_SESSION_TYPE_LABELS,
  type CanonicalDecisionClassKind,
  type CanonicalSessionType,
} from '../../../types/prioritization/session';
import { shortDate } from './plan';

/** The right, named the way the Decision Rights matrix names it: "Items / Selection". */
export function delegationRightLabel(d: Pick<MyDelegation, 'domain' | 'decisionClass'>): string {
  const domain = CANONICAL_SESSION_TYPE_LABELS[d.domain as CanonicalSessionType] ?? d.domain;
  const cls = CANONICAL_DECISION_CLASS_LABELS[d.decisionClass as CanonicalDecisionClassKind] ?? d.decisionClass;
  return `${domain} / ${cls}`;
}

/**
 * When a delegation ends: "Expired Oct 20", "Expires in 5 days · Nov 2",
 * "Expires in under a day · Oct 29", "No end date — stands until revoked",
 * "Not in force until accepted".
 */
export function delegationTermLine(d: Pick<MyDelegation, 'status' | 'daysLeft' | 'expiresAt'>): string {
  const on = shortDate(d.expiresAt);
  if (d.status === 'revoked') return 'Revoked';
  if (d.status === 'expired') return on ? `Expired ${on}` : 'Expired';
  if (d.status === 'awaiting_acceptance') {
    return on ? `Not in force until accepted · would end ${on}` : 'Not in force until accepted';
  }
  if (!on) return 'No end date — stands until revoked';
  if (d.daysLeft === null || d.daysLeft < 1) return `Expires in under a day · ${on}`;
  return `Expires in ${d.daysLeft} ${d.daysLeft === 1 ? 'day' : 'days'} · ${on}`;
}

/** Coral once expired, mustard inside a week, otherwise quiet. */
export function delegationTermTone(d: Pick<MyDelegation, 'status' | 'daysLeft'>): 'coral' | 'mustard' | 'quiet' {
  if (d.status === 'expired') return 'coral';
  if (d.status === 'in_force' && d.daysLeft !== null && d.daysLeft < 7) return 'mustard';
  return 'quiet';
}

/** Who the delegation came from or went to, for the row's second line. */
export function delegationParty(d: Pick<MyDelegation, 'direction' | 'delegateName' | 'delegatedByName' | 'cycleName'>): string {
  const cycle = d.cycleName ? ` · ${d.cycleName}` : '';
  return d.direction === 'to_me'
    ? `From ${d.delegatedByName ?? 'someone no longer named'}${cycle}`
    : `To ${d.delegateName ?? 'someone no longer named'}${cycle}`;
}
